import dayjs from 'dayjs'
import { TimeRange } from 'features/systemLens'
import { computeLiveTailWindow } from 'features/timeSeries/utils/liveTailWindow'
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useInterval } from 'react-use'
import { ChartTimeUnit, DateMode } from 'types'

const DEFAULT_CADENCE_SECONDS = 30

interface UseDateModeProps {
  setTimeRange: Dispatch<SetStateAction<TimeRange>>
  timeRange: TimeRange
  initialCadenceSeconds?: number
  initialDateMode?: DateMode
  relativeStart?: { value?: number; unit: ChartTimeUnit }
  relativeEnd?: { isNow: boolean; value?: number; unit: ChartTimeUnit }
  isLoadingRef?: MutableRefObject<boolean>
}

export const useDateMode = ({
  setTimeRange,
  timeRange,
  initialCadenceSeconds,
  initialDateMode,
  relativeStart,
  relativeEnd,
  isLoadingRef,
}: UseDateModeProps) => {
  const [dateMode, internalSetDateMode] = useState<DateMode>(
    initialDateMode ?? 'relative'
  )
  const [liveTail, setLiveTail] = useState(false)
  const [cadenceSeconds, setCadenceSeconds] = useState<number>(
    initialCadenceSeconds ?? DEFAULT_CADENCE_SECONDS
  )

  const lastAbsoluteTimeRangeRef = useRef<TimeRange | null>(null)
  const lastRelativeTimeRangeRef = useRef<TimeRange | null>(null)

  // Mirrors `dateMode` so `setDateMode` can resolve a functional updater
  // synchronously at call time (see below) — including across two calls in
  // the same batch, before React has re-rendered.
  const dateModeRef = useRef(dateMode)
  useEffect(() => {
    dateModeRef.current = dateMode
  }, [dateMode])

  useEffect(() => {
    if (dateMode === 'absolute') {
      lastAbsoluteTimeRangeRef.current = timeRange
    } else if (dateMode === 'relative') {
      lastRelativeTimeRangeRef.current = timeRange
    }
  }, [timeRange, dateMode])

  const setDateMode: Dispatch<SetStateAction<DateMode>> = useCallback(
    (updater) => {
      const newDateMode =
        typeof updater === 'function' ? updater(dateModeRef.current) : updater
      dateModeRef.current = newDateMode
      internalSetDateMode(newDateMode)

      // The liveTail/timeRange side effects run HERE, synchronously at call
      // time — deliberately NOT inside the state updater. Inside an updater
      // they get queued during the render pass, which lands them AFTER any
      // `setLiveTail`/`setTimeRange` the caller issued right after this call,
      // silently clobbering them (`resetViewport` lost its explicit range and
      // liveTail:false to this restore). Queued at call time, a caller's
      // subsequent setters queue later and win — the mode toggle keeps its
      // restore behaviour when called alone.
      setLiveTail(newDateMode === 'relative')

      if (newDateMode === 'absolute' && lastAbsoluteTimeRangeRef.current) {
        setTimeRange(lastAbsoluteTimeRangeRef.current)
      } else if (
        newDateMode === 'relative' &&
        lastRelativeTimeRangeRef.current
      ) {
        setTimeRange(lastRelativeTimeRangeRef.current)
      }
    },
    [setTimeRange]
  )

  /**
   * Leave live tail because the user navigated to a specific window.
   *
   * Deliberately NOT `setDateMode('absolute')`. That is the *mode toggle*: it
   * restores `lastAbsoluteTimeRangeRef` so flipping absolute/relative returns
   * you to the window you last had in that mode. Navigation is the opposite
   * intent — the caller is setting the window itself, and the restore would
   * replace it with a stale absolute window — often one from a much earlier
   * session, with no data in it.
   */
  const exitLiveTail = useCallback(() => {
    dateModeRef.current = 'absolute'
    internalSetDateMode('absolute')
    setLiveTail(false)
  }, [])

  useEffect(() => {
    if (dateMode === 'relative' && liveTail && relativeStart?.value) {
      setTimeRange((prev) => {
        const { from: nextFrom, to: nextTo } = computeLiveTailWindow({
          now: dayjs(),
          relativeStart: {
            value: relativeStart.value!,
            unit: relativeStart.unit,
          },
          relativeEnd,
          interval: prev.interval,
          intervalUnit: prev.intervalUnit,
        })
        if (prev.from === nextFrom && prev.to === nextTo) {
          return prev
        }
        return { ...prev, from: nextFrom, to: nextTo }
      })
    }
    // `relativeEnd` is provider useState, so its identity is stable between
    // edits — depending on the object itself does not re-fire this effect.
  }, [
    dateMode,
    liveTail,
    relativeStart?.value,
    relativeStart?.unit,
    relativeEnd,
    setTimeRange,
  ])

  useInterval(() => {
    // Live tail only re-anchors a *relative* window. An absolute window is a
    // span the user pinned, so dragging it to "now" here silently rewrites the
    // range they are about to save (Bug 1). This mirrors the effect above,
    // which already gates on `dateMode === 'relative'`; the interval must too,
    // because `liveTail` can be turned on independently of the mode.
    if (dateMode !== 'relative' || !liveTail || !relativeStart?.value) {
      return
    }
    // Skip this tick if a fetch is still in-flight to avoid cancelling it
    if (isLoadingRef?.current) {
      return
    }

    setTimeRange((prev) => {
      const { from: nextFrom, to: nextTo } = computeLiveTailWindow({
        now: dayjs(),
        relativeStart: {
          value: relativeStart.value!,
          unit: relativeStart.unit,
        },
        relativeEnd,
        interval: prev.interval,
        intervalUnit: prev.intervalUnit,
      })

      // Bail out when the window has not actually moved. Returning `prev`
      // unchanged lets React skip the re-render entirely, so no refetch is
      // triggered and the chart is never torn down. Most ticks land here: at a
      // 10s cadence with 1-minute buckets, only one tick in six does any work.
      if (prev.from === nextFrom && prev.to === nextTo) {
        return prev
      }

      return { ...prev, from: nextFrom, to: nextTo }
    })
  }, cadenceSeconds * 1000)

  return {
    dateMode,
    setDateMode,
    exitLiveTail,
    liveTail,
    setLiveTail,
    cadenceSeconds,
    setCadenceSeconds,
  }
}
